import React, { useCallback, useMemo, useRef, useState } from "react";
import DataGrid, { Row } from "react-data-grid";
import { Button, Empty, Popconfirm, Tooltip, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { createCellEditor } from "../EditableCellGrid";
import { writeExcelFriendlyTableToClipboard } from "../../../utils/clipboardTableUtils";
import "react-data-grid/lib/styles.css";
import "./SpreadsheetGrid.css";

const { Text } = Typography;

const ROW_NUMBER_COLUMN_KEY = "__spreadsheet_row_number";
const ACTION_COLUMN_KEY = "__spreadsheet_actions";
const DEFAULT_HEADER_ROW_HEIGHT = 38;
const DEFAULT_ROW_HEIGHT = 40;
const DEFAULT_GRID_FRAME_HEIGHT = 2;
const CLICK_TO_EDIT_DRAG_THRESHOLD_PX = 4;

function joinClassNames(...classNames) {
    return classNames.filter(Boolean).join(" ") || undefined;
}

function resolveClassName(className, row) {
    return typeof className === "function" ? className(row) : className;
}

function isTextInputTarget(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest("input, textarea, select, [contenteditable='true'], .ant-select, .ant-select-dropdown"));
}

function getEventCellPosition(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const rowElement = target.closest(".rdg-row[data-rdg-row-idx]");
    const cellElement = target.closest(".rdg-cell");
    if (!rowElement || !cellElement) return null;

    const rowIdx = Number(rowElement.getAttribute("data-rdg-row-idx"));
    const colIdx = Number(cellElement.getAttribute("aria-colindex")) - 1;
    if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return null;

    return { rowIdx, colIdx };
}

function getCellKey(row, column, rowKeyGetter) {
    if (!row || !column?.key || column.key === ROW_NUMBER_COLUMN_KEY || column.key === ACTION_COLUMN_KEY || column.selectable === false) {
        return null;
    }
    const rowKey = rowKeyGetter(row);
    if (rowKey === null || rowKey === undefined) return null;
    return `${rowKey}:${column.key}`;
}

function normalizeRange(range) {
    if (!range) return null;
    return {
        startRowIdx: Math.min(range.startRowIdx, range.endRowIdx),
        endRowIdx: Math.max(range.startRowIdx, range.endRowIdx),
        startColIdx: Math.min(range.startColIdx, range.endColIdx),
        endColIdx: Math.max(range.startColIdx, range.endColIdx),
    };
}

function getCellsInRange(rows, columns, range, rowKeyGetter) {
    const normalizedRange = normalizeRange(range);
    const cells = new Set();
    if (!normalizedRange) return cells;

    for (let rowIdx = normalizedRange.startRowIdx; rowIdx <= normalizedRange.endRowIdx && rowIdx < rows.length; rowIdx += 1) {
        for (let colIdx = normalizedRange.startColIdx; colIdx <= normalizedRange.endColIdx && colIdx < columns.length; colIdx += 1) {
            const cellKey = getCellKey(rows[rowIdx], columns[colIdx], rowKeyGetter);
            if (cellKey) cells.add(cellKey);
        }
    }
    return cells;
}

function formatCopyValue(row, column) {
    if (column.copyFormatter) return column.copyFormatter(row[column.key], row);
    const value = row[column.key];
    if (value === null || value === undefined) return "";
    return value;
}

function DefaultCellContent({ row, column, error }) {
    const value = row[column.key];
    const content = column.renderValue ? column.renderValue(value, row) : value;
    const display = content === null || content === undefined || content === "" ? <Text type="secondary">-</Text> : content;
    if (!error) return <span className="spreadsheet-grid-cell-content">{display}</span>;
    return (
        <Tooltip title={error} placement="topLeft">
            <span className="spreadsheet-grid-cell-content spreadsheet-grid-cell-content-error">{display}</span>
        </Tooltip>
    );
}

const SpreadsheetGrid = ({
    rows = [],
    columns = [],
    onRowsChange,
    rowKeyGetter = (row) => row.id,
    readOnly = false,
    getCellError,
    onDeleteRow,
    deleteConfirmTitle = "确定删除这一行吗？",
    emptyDescription = "暂无数据",
    className,
    rowHeight = DEFAULT_ROW_HEIGHT,
    headerRowHeight = DEFAULT_HEADER_ROW_HEIGHT,
}) => {
    const [selectedCells, setSelectedCells] = useState(new Set());
    const [selectedRange, setSelectedRange] = useState(null);
    const isSelectingRef = useRef(false);
    const selectionAnchorRef = useRef(null);
    const clickToEditCandidateRef = useRef(null);
    const activeColumnsRef = useRef([]);

    const rowNumberColumn = useMemo(
        () => ({
            key: ROW_NUMBER_COLUMN_KEY,
            name: "#",
            width: 46,
            minWidth: 46,
            maxWidth: 46,
            resizable: false,
            sortable: false,
            selectable: false,
            headerCellClass: "spreadsheet-grid-row-number-header-cell",
            cellClass: "spreadsheet-grid-row-number-cell",
            renderCell: ({ rowIdx }) => <span className="spreadsheet-grid-row-number">{rowIdx + 1}</span>,
        }),
        [],
    );

    const actionColumn = useMemo(() => {
        if (readOnly || !onDeleteRow) return null;
        return {
            key: ACTION_COLUMN_KEY,
            name: "操作",
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            resizable: false,
            sortable: false,
            selectable: false,
            headerCellClass: "spreadsheet-grid-action-header-cell",
            cellClass: "spreadsheet-grid-action-cell",
            renderCell: ({ row }) => (
                <Popconfirm title={deleteConfirmTitle} okText="删除" cancelText="取消" onConfirm={() => onDeleteRow(row)}>
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                    />
                </Popconfirm>
            ),
        };
    }, [deleteConfirmTitle, onDeleteRow, readOnly]);

    const displayColumns = useMemo(() => {
        const businessColumns = columns.map((column) => {
            const originalCellClass = column.cellClass;
            const originalHeaderCellClass = column.headerCellClass;
            const editable = !readOnly && column.editable !== false;
            const renderEditCell = editable && column.editorType ? createCellEditor(column.editorType) : undefined;
            return {
                ...column,
                name: column.name || column.title || column.key,
                resizable: column.resizable !== false,
                renderCell: (props) => {
                    const error = getCellError?.(props.row, column.key);
                    if (column.renderCell) return column.renderCell({ ...props, error });
                    return <DefaultCellContent row={props.row} column={column} error={error} />;
                },
                renderEditCell,
                headerCellClass: joinClassNames(originalHeaderCellClass, "spreadsheet-grid-header-cell"),
                cellClass: (row) => {
                    const error = getCellError?.(row, column.key);
                    const cellKey = getCellKey(row, column, rowKeyGetter);
                    return joinClassNames(
                        resolveClassName(originalCellClass, row),
                        error && "spreadsheet-grid-cell-error",
                        cellKey && selectedCells.has(cellKey) && "spreadsheet-grid-cell-selected",
                    );
                },
            };
        });
        return [rowNumberColumn, ...businessColumns, ...(actionColumn ? [actionColumn] : [])];
    }, [actionColumn, columns, getCellError, readOnly, rowKeyGetter, rowNumberColumn, selectedCells]);

    activeColumnsRef.current = displayColumns;

    const clearSelection = useCallback(() => {
        setSelectedCells(new Set());
        setSelectedRange(null);
        isSelectingRef.current = false;
        selectionAnchorRef.current = null;
        clickToEditCandidateRef.current = null;
    }, []);

    const applyRangeSelection = useCallback(
        (startPosition, endPosition) => {
            const range = {
                startRowIdx: startPosition.rowIdx,
                startColIdx: startPosition.colIdx,
                endRowIdx: endPosition.rowIdx,
                endColIdx: endPosition.colIdx,
            };
            setSelectedRange(range);
            setSelectedCells(getCellsInRange(rows, activeColumnsRef.current, range, rowKeyGetter));
        },
        [rowKeyGetter, rows],
    );

    const handleMouseDown = useCallback(
        (event) => {
            if (event.button !== 0 || isTextInputTarget(event.target)) return;
            const position = getEventCellPosition(event);
            if (!position) {
                clearSelection();
                return;
            }

            const row = rows[position.rowIdx];
            const column = activeColumnsRef.current[position.colIdx];
            const cellKey = getCellKey(row, column, rowKeyGetter);
            if (!cellKey) {
                clearSelection();
                return;
            }

            const isSingleSelectedCell = selectedCells.size === 1 && selectedCells.has(cellKey);
            clickToEditCandidateRef.current = isSingleSelectedCell
                ? { cellKey, clientX: event.clientX, clientY: event.clientY }
                : null;

            selectionAnchorRef.current = position;
            isSelectingRef.current = true;
            applyRangeSelection(position, position);
        },
        [applyRangeSelection, clearSelection, rowKeyGetter, rows, selectedCells],
    );

    const handleMouseMove = useCallback(
        (event) => {
            const candidate = clickToEditCandidateRef.current;
            if (candidate && (Math.abs(event.clientX - candidate.clientX) > CLICK_TO_EDIT_DRAG_THRESHOLD_PX || Math.abs(event.clientY - candidate.clientY) > CLICK_TO_EDIT_DRAG_THRESHOLD_PX)) {
                clickToEditCandidateRef.current = null;
            }

            if (!isSelectingRef.current || !selectionAnchorRef.current) return;
            const position = getEventCellPosition(event);
            if (!position) return;
            applyRangeSelection(selectionAnchorRef.current, position);
        },
        [applyRangeSelection],
    );

    const handleMouseUp = useCallback(() => {
        isSelectingRef.current = false;
        selectionAnchorRef.current = null;
    }, []);

    const copySelectedRange = useCallback(async () => {
        const range = normalizeRange(selectedRange);
        if (!range) return false;
        const activeColumns = activeColumnsRef.current;
        const copyableColumnIndexes = [];
        for (let colIdx = range.startColIdx; colIdx <= range.endColIdx && colIdx < activeColumns.length; colIdx += 1) {
            const column = activeColumns[colIdx];
            if (column?.key && column.key !== ROW_NUMBER_COLUMN_KEY && column.key !== ACTION_COLUMN_KEY && column.copyable !== false) {
                copyableColumnIndexes.push(colIdx);
            }
        }
        if (!copyableColumnIndexes.length) return false;

        const tableRows = [];
        for (let rowIdx = range.startRowIdx; rowIdx <= range.endRowIdx && rowIdx < rows.length; rowIdx += 1) {
            const row = rows[rowIdx];
            tableRows.push(copyableColumnIndexes.map((colIdx) => formatCopyValue(row, activeColumns[colIdx])));
        }
        if (!tableRows.length) return false;
        try {
            await writeExcelFriendlyTableToClipboard(tableRows);
            return true;
        } catch (error) {
            console.warn("Failed to copy spreadsheet grid range:", error);
            return false;
        }
    }, [rows, selectedRange]);

    const handleKeyDownCapture = useCallback(
        (event) => {
            if (event.key === "Escape" && selectedCells.size && !isTextInputTarget(event.target)) {
                event.preventDefault();
                event.stopPropagation();
                clearSelection();
                return;
            }

            const isCopy = (event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === "c";
            if (!isCopy || isTextInputTarget(event.target)) return;
            event.preventDefault();
            event.stopPropagation();
            void copySelectedRange();
        },
        [clearSelection, copySelectedRange, selectedCells.size],
    );

    const handleRowsChange = useCallback(
        (nextRows, data) => {
            onRowsChange?.(nextRows, data);
        },
        [onRowsChange],
    );

    const renderRow = useCallback((key, props) => (
        <Row key={key} {...props} data-rdg-row-idx={props.rowIdx} data-rdg-row-key={rowKeyGetter(props.row)} />
    ), [rowKeyGetter]);

    const renderers = useMemo(() => ({ renderRow }), [renderRow]);
    const gridHeight = headerRowHeight + Math.max(rows.length, 1) * rowHeight + DEFAULT_GRID_FRAME_HEIGHT;

    return (
        <div className={joinClassNames("spreadsheet-grid", className)}>
            <div
                className="spreadsheet-grid-shell"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onKeyDownCapture={handleKeyDownCapture}
            >
                <DataGrid
                    className="spreadsheet-grid-rdg"
                    style={{ blockSize: gridHeight }}
                    columns={displayColumns}
                    rows={rows}
                    rowKeyGetter={rowKeyGetter}
                    rowHeight={rowHeight}
                    headerRowHeight={headerRowHeight}
                    onRowsChange={handleRowsChange}
                    renderers={renderers}
                    onCellClick={(args) => {
                        const cellKey = getCellKey(args.row, args.column, rowKeyGetter);
                        const candidate = clickToEditCandidateRef.current;
                        clickToEditCandidateRef.current = null;
                        if (!readOnly && args.column?.renderEditCell && cellKey && candidate?.cellKey === cellKey) {
                            args.selectCell(true);
                        }
                    }}
                    onCellDoubleClick={(args) => {
                        if (!readOnly && args.column?.renderEditCell) {
                            args.selectCell(true);
                        }
                    }}
                    defaultColumnOptions={{ resizable: true }}
                />
            </div>
            {rows.length === 0 && (
                <div className="spreadsheet-grid-empty">
                    <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            )}
        </div>
    );
};

export default SpreadsheetGrid;
